import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_BLUEPRINTS, GET_BLUEPRINT_VERSIONS } from '../graphql/queries';
import { DataTable } from '../components/DataTable';
import OsIcon from '../components/OsIcon';
import { formatArch } from '../utils/os-icons';

const columns = [
  { accessorKey: 'name', header: 'Name', cell: ({ getValue }) => <OsIcon name={getValue()} showLabel /> },
  { accessorKey: 'version', header: 'Version' },
  {
    accessorKey: 'osType',
    header: 'OS',
    cell: ({ row }) => <OsIcon name={row.original.name || row.original.osType} size={18} />,
  },
  { accessorKey: 'architecture', header: 'Arch', cell: ({ getValue }) => formatArch(getValue()) },
  { accessorKey: 'description', header: 'Description', cell: ({ getValue }) => getValue() || '-' },
  {
    accessorKey: 'lastBuildAt',
    header: 'Last Build',
    cell: ({ getValue }) => getValue() ? new Date(getValue()).toLocaleDateString() : '-',
  },
  { accessorKey: 'lastAmiId', header: 'Last AMI', cell: ({ getValue }) => getValue() || '-' },
];

export default function Blueprints() {
  const { data, loading, error } = useQuery(GET_BLUEPRINTS);
  const { data: versionData } = useQuery(GET_BLUEPRINT_VERSIONS);

  if (loading) return <div className="loading">Loading blueprints...</div>;
  if (error) return <div className="error">Failed to load blueprints: {error.message}</div>;

  const blueprints = data?.listBlueprints?.items || [];
  const versions = versionData?.listBlueprintVersions || [];

  return (
    <div>
      <h2 className="page-title">Blueprints</h2>

      <div className="card">
        <h3>Blueprint Versions</h3>
        <p style={{ margin: '8px 0' }}>
          Blueprints are managed via the <strong>image-factory-blueprints</strong> Git repository.
          Push to <code>main</code> to sync the latest version. Create a Git tag (e.g. <code>v1.0.0</code>) to create an immutable versioned release.
        </p>
        {versions.length > 0 ? (
          <div style={{ margin: '8px 0' }}>
            <strong>Available versions:</strong>{' '}
            {versions.map((v, i) => (
              <span key={v} className="badge badge-info" style={{ marginRight: 4 }}>{v}</span>
            ))}
          </div>
        ) : (
          <p style={{ color: '#888' }}>No tagged versions yet. The latest synced blueprints from main are being used.</p>
        )}
        <div style={{ marginTop: 12 }}>
          <Link to="/build" className="btn btn-primary" style={{ fontSize: 13, padding: '6px 16px' }}>Trigger a Build</Link>
        </div>
      </div>

      <div className="card">
        <h3>Available Blueprints</h3>
        {blueprints.length === 0 ? (
          <p>No blueprints found. Push blueprints to the repository to get started.</p>
        ) : (
          <DataTable columns={columns} data={blueprints} searchPlaceholder="Search blueprints..." />
        )}
      </div>
    </div>
  );
}
